import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faRobot } from '@fortawesome/free-solid-svg-icons'
import ReactMarkdown from 'react-markdown'
import { useChat } from '../../context/ChatContext'
import profile from '../../data/profiles/activeProfile'
import './index.scss'

const { widgetTitle, quickPrompts: QUICK_PROMPTS } = profile.ai

const titleWords = widgetTitle.split(' ')
const titleLead = titleWords.slice(0, -1).join(' ')
const titleEm = titleWords[titleWords.length - 1]

const TypingIndicator = () => (
  <div className="widget-message widget-message--ai">
    <div className="widget-typing">
      <span /><span /><span />
    </div>
  </div>
)

const ChatWidget = () => {
  const [input, setInput] = useState('')
  const { messages, isLoading, sendMessage, isOpen, closeChat, toggleChat } = useChat()
  const bottomRef = useRef(null)
  const inputRef = useRef(null)
  const location = useLocation()
  const isAiPage = location.pathname === '/ai'

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!input.trim()) return
    sendMessage(input.trim())
    setInput('')
  }

  const handleQuickPrompt = (text) => {
    sendMessage(text)
  }

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150)
  }, [isOpen])

  // Don't render widget on the /ai page (all hooks are above this)
  if (isAiPage) return null

  return (
    <>
      {/* Floating action button */}
      <button
        className={`chat-fab ${isOpen ? 'chat-fab--open' : ''}`}
        onClick={toggleChat}
        aria-label={isOpen ? 'Close AI assistant' : 'Open AI assistant'}
        data-cursor={isOpen ? 'close' : 'ask ai'}
      >
        {isOpen ? (
          <FontAwesomeIcon icon={faTimes} />
        ) : (
          <FontAwesomeIcon icon={faRobot} />
        )}
        {!isOpen && <span className="chat-fab__live" aria-hidden="true" />}
      </button>

      {/* Widget panel */}
      {isOpen && (
        <div className="chat-widget" role="dialog" aria-label="AI Assistant">

          {/* Header */}
          <div className="chat-widget__header">
            <div className="chat-widget__header-left">
              <span className="chat-widget__dot" />
              <span className="chat-widget__title">
                {titleLead} <em>{titleEm}</em>
              </span>
            </div>
            <div className="chat-widget__header-actions">
              <Link
                to="/ai"
                className="chat-widget__expand"
                onClick={closeChat}
                title="Open full page"
              >
                <span>Full view</span>
                <span aria-hidden="true">↗</span>
              </Link>
              <button
                className="chat-widget__close"
                onClick={closeChat}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chat-widget__body">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`widget-message widget-message--${msg.role}`}
              >
                <div className="widget-message__bubble">
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              </div>
            ))}
            {isLoading && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          {/* Quick prompts */}
          <div className="chat-widget__chips">
            {QUICK_PROMPTS.map(({ label, text }) => (
              <button
                key={label}
                className="chat-widget__chip"
                onClick={() => handleQuickPrompt(text)}
                disabled={isLoading}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Input */}
          <form className="chat-widget__footer" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              disabled={isLoading}
              className="chat-widget__input"
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="chat-widget__send"
              aria-label="Send"
            >
              ↗
            </button>
          </form>
        </div>
      )}
    </>
  )
}

export default ChatWidget
